import { useEffect, useMemo, useState } from 'react';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Link,
  NumberInput,
  NumberInputField,
  Radio,
  RadioGroup,
  Text,
  Textarea,
  VStack,
} from '@chakra-ui/react';
import { AddIcon, CloseIcon } from '@chakra-ui/icons';

import type { MvrCharacterType, MvrFormatRuleSpec, MvrLengthSpec } from 'api';

import {
  buildRegexAndDescription,
  type MvrFormatBuildResult,
} from '../regexBuilder';

interface MvrFormatRuleBuilderProps {
  initialValue: MvrFormatRuleSpec;
  initialDescription: string;
  onChange: (
    spec: MvrFormatRuleSpec,
    result: MvrFormatBuildResult,
    description: string,
    incomplete: boolean,
  ) => void;
}

const CHARACTER_TYPE_OPTIONS: { value: MvrCharacterType; label: string }[] = [
  { value: 'digits', label: 'Digits only' },
  { value: 'letters', label: 'Letters only' },
  { value: 'alphanumeric', label: 'Alphanumeric' },
];

const useOptionListField = (initial: string[] | null | undefined) => {
  const [enabled, setEnabled] = useState(() => !!initial?.length);
  const [options, setOptions] = useState<string[]>(() =>
    initial?.length ? [...initial] : [''],
  );

  return {
    enabled,
    setEnabled,
    options,
    isBlank: options.every((o) => !o.trim()),
    add: () => setOptions((prev) => [...prev, '']),
    remove: (index: number) =>
      setOptions((prev) => prev.filter((_, i) => i !== index)),
    update: (index: number, value: string) =>
      setOptions((prev) => prev.map((o, i) => (i === index ? value : o))),
  };
};

const OptionListField = ({
  label,
  placeholder,
  field,
}: {
  label: string;
  placeholder: string;
  field: ReturnType<typeof useOptionListField>;
}) => (
  <FormControl>
    <Checkbox
      isChecked={field.enabled}
      onChange={(e) => field.setEnabled(e.target.checked)}
    >
      {label}
    </Checkbox>
    {field.enabled && (
      <VStack align="stretch" spacing={2} mt={2}>
        {field.options.map((option, index) => (
          <HStack key={index}>
            <Input
              variant="main"
              size="sm"
              value={option}
              placeholder={placeholder}
              onChange={(e) => field.update(index, e.target.value)}
            />
            <IconButton
              aria-label="Remove option"
              icon={<CloseIcon boxSize={2.5} />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => field.remove(index)}
              isDisabled={field.options.length <= 1}
            />
          </HStack>
        ))}
        <Button
          size="sm"
          variant="ghost"
          leftIcon={<AddIcon boxSize={2.5} />}
          onClick={field.add}
          alignSelf="flex-start"
        >
          Add option
        </Button>
      </VStack>
    )}
  </FormControl>
);

const MvrFormatRuleBuilder = ({
  initialValue,
  initialDescription,
  onChange,
}: MvrFormatRuleBuilderProps) => {
  const startsWith = useOptionListField(initialValue.startsWith);
  const endsWith = useOptionListField(initialValue.endsWith);

  const [lengthEnabled, setLengthEnabled] = useState(
    () => !!initialValue.length,
  );
  const [lengthMode, setLengthMode] = useState<'exact' | 'range'>(
    () => initialValue.length?.mode ?? 'exact',
  );
  const [lengthExact, setLengthExact] = useState(() =>
    initialValue.length?.mode === 'exact'
      ? String(initialValue.length.value)
      : '',
  );
  const [lengthMin, setLengthMin] = useState(() =>
    initialValue.length?.mode === 'range' && initialValue.length.min != null
      ? String(initialValue.length.min)
      : '',
  );
  const [lengthMax, setLengthMax] = useState(() =>
    initialValue.length?.mode === 'range' && initialValue.length.max != null
      ? String(initialValue.length.max)
      : '',
  );

  const [characterTypeEnabled, setCharacterTypeEnabled] = useState(
    () => !!initialValue.characterType,
  );
  const [characterType, setCharacterType] = useState<MvrCharacterType | null>(
    () => initialValue.characterType ?? null,
  );

  const length = useMemo<MvrLengthSpec | null>(() => {
    if (!lengthEnabled) return null;
    if (lengthMode === 'exact') {
      const value = Number(lengthExact);
      return {
        mode: 'exact',
        value: lengthExact.trim() && Number.isFinite(value) ? value : NaN,
      };
    }
    const min = lengthMin.trim() ? Number(lengthMin) : null;
    const max = lengthMax.trim() ? Number(lengthMax) : null;
    return { mode: 'range', min, max };
  }, [lengthEnabled, lengthMode, lengthExact, lengthMin, lengthMax]);

  const spec = useMemo<MvrFormatRuleSpec>(
    () => ({
      startsWith: startsWith.enabled ? startsWith.options : null,
      endsWith: endsWith.enabled ? endsWith.options : null,
      length,
      characterType: characterTypeEnabled ? characterType : null,
    }),
    [
      startsWith.enabled,
      startsWith.options,
      endsWith.enabled,
      endsWith.options,
      length,
      characterTypeEnabled,
      characterType,
    ],
  );

  const buildResult = useMemo(() => buildRegexAndDescription(spec), [spec]);

  const lengthIncomplete =
    lengthEnabled &&
    (lengthMode === 'exact'
      ? Number.isNaN(length?.mode === 'exact' ? length.value : NaN)
      : !lengthMin.trim() && !lengthMax.trim());
  const incomplete =
    (startsWith.enabled && startsWith.isBlank) ||
    (endsWith.enabled && endsWith.isBlank) ||
    lengthIncomplete ||
    (characterTypeEnabled && !characterType) ||
    buildResult.blockingErrors.length > 0;

  const [manualDescription, setManualDescription] =
    useState(initialDescription);
  const [descriptionTouched, setDescriptionTouched] = useState(
    () => !!initialDescription.trim(),
  );
  const description = descriptionTouched
    ? manualDescription
    : buildResult.description;

  const useGeneratedDescription = () => {
    setManualDescription(buildResult.description);
    setDescriptionTouched(false);
  };

  useEffect(() => {
    onChange(spec, buildResult, description, incomplete);
  }, [spec, buildResult, description, incomplete, onChange]);

  const [testValue, setTestValue] = useState('');
  const testMatch = useMemo(() => {
    if (!buildResult.regex || !testValue.trim()) return null;
    try {
      return new RegExp(buildResult.regex, 'i').test(testValue);
    } catch {
      return null;
    }
  }, [buildResult.regex, testValue]);

  return (
    <VStack align="stretch" spacing={4}>
      <OptionListField
        label="Starts with"
        placeholder="e.g. 1"
        field={startsWith}
      />
      <OptionListField
        label="Ends with"
        placeholder="e.g. X"
        field={endsWith}
      />

      <FormControl>
        <Checkbox
          isChecked={lengthEnabled}
          onChange={(e) => setLengthEnabled(e.target.checked)}
        >
          Length
        </Checkbox>
        {lengthEnabled && (
          <Box mt={2}>
            <RadioGroup
              value={lengthMode}
              onChange={(v) => setLengthMode(v as 'exact' | 'range')}
            >
              <HStack spacing={4} mb={2}>
                <Radio value="exact">Exact</Radio>
                <Radio value="range">Range</Radio>
              </HStack>
            </RadioGroup>
            {lengthMode === 'exact' ? (
              <NumberInput
                variant="main"
                size="sm"
                min={1}
                precision={0}
                value={lengthExact}
                onChange={(v) => setLengthExact(v)}
                maxW="120px"
              >
                <NumberInputField placeholder="Characters" />
              </NumberInput>
            ) : (
              <HStack>
                <NumberInput
                  variant="main"
                  size="sm"
                  min={0}
                  precision={0}
                  value={lengthMin}
                  onChange={(v) => setLengthMin(v)}
                  maxW="100px"
                >
                  <NumberInputField placeholder="Min" />
                </NumberInput>
                <Text>to</Text>
                <NumberInput
                  variant="main"
                  size="sm"
                  min={0}
                  precision={0}
                  value={lengthMax}
                  onChange={(v) => setLengthMax(v)}
                  maxW="100px"
                >
                  <NumberInputField placeholder="Max" />
                </NumberInput>
              </HStack>
            )}
          </Box>
        )}
      </FormControl>

      <FormControl>
        <Checkbox
          isChecked={characterTypeEnabled}
          onChange={(e) => setCharacterTypeEnabled(e.target.checked)}
        >
          Restrict character type
        </Checkbox>
        {characterTypeEnabled && (
          <RadioGroup
            mt={2}
            value={characterType ?? ''}
            onChange={(v) => setCharacterType(v as MvrCharacterType)}
          >
            <HStack spacing={4}>
              {CHARACTER_TYPE_OPTIONS.map((opt) => (
                <Radio key={opt.value} value={opt.value}>
                  {opt.label}
                </Radio>
              ))}
            </HStack>
          </RadioGroup>
        )}
      </FormControl>

      {buildResult.regex && (
        <>
          <FormControl>
            <FormLabel
              fontSize="sm"
              mb={1}
              display="flex"
              justifyContent="space-between"
            >
              <span>Format description</span>
              {descriptionTouched && buildResult.description && (
                <Link
                  fontSize="xs"
                  cursor="pointer"
                  onClick={useGeneratedDescription}
                >
                  Use generated description
                </Link>
              )}
            </FormLabel>
            <Textarea
              variant="main"
              h="80px"
              resize="none"
              value={description}
              onChange={(e) => {
                setManualDescription(e.target.value);
                setDescriptionTouched(true);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel fontSize="sm" mb={1}>
              Test a sample value
            </FormLabel>
            <Input
              variant="main"
              size="sm"
              value={testValue}
              placeholder="e.g. 1234567"
              onChange={(e) => setTestValue(e.target.value)}
              isInvalid={testMatch === false}
              borderWidth={testMatch === null ? undefined : '2px'}
              borderColor={testMatch === true ? 'green.500' : undefined}
              boxShadow={
                testMatch === true
                  ? '0 0 0 1px var(--chakra-colors-green-500)'
                  : testMatch === false
                    ? '0 0 0 1px var(--chakra-colors-red-600)'
                    : undefined
              }
              _hover={
                testMatch === true ? { borderColor: 'green.500' } : undefined
              }
              _focus={
                testMatch === true
                  ? {
                      borderColor: 'green.500',
                      boxShadow: '0 0 0 1px var(--chakra-colors-green-500)',
                    }
                  : undefined
              }
            />
          </FormControl>
        </>
      )}

      {buildResult.blockingErrors.map((message) => (
        <Alert key={message} status="error" fontSize="sm" borderRadius="md">
          <AlertIcon />
          {message}
        </Alert>
      ))}

      {buildResult.warnings.map((message) => (
        <Alert key={message} status="warning" fontSize="sm" borderRadius="md">
          <AlertIcon />
          {message}
        </Alert>
      ))}
    </VStack>
  );
};

export default MvrFormatRuleBuilder;
